import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import NotyfContext from "../../../contexts/NotyfContext";
import { useWhitelabel } from "../../../contexts/WhitelabelContext";
import useDeviceType from "../../../hooks/useDeviceType";
import * as Yup from "yup";
import { Card, Form, Button, Collapse, Row, Col } from "react-bootstrap";
import { Formik } from "formik";
import Loader from "../../../components/spinners/Loader";
import {
  getCrmConfigurationPreferences,
  getInvoiceSequence,
  updateCrmConfigPreferences,
  updateInvoiceSequence,
} from "../../../redux/slices/preferences";

const CrmConfigurationSettings = ({ activeTab }) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user.user);
  const impersonationUser = useSelector(
    (state) => state.impersonationUser.impersonationUser
  );
  const [activeUser, setActiveUser] = useState({});
  const crmConfigurationPreferences = useSelector(
    (state) => state.preferences.crmConfigurationPreferences
  );
  const invoiceSequence = useSelector(
    (state) => state.preferences.invoiceSequence
  );
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const notyf = useContext(NotyfContext);
  const { backgroundColor, fontColor, accentColor } = useWhitelabel();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [user, impersonationUser]);

  useEffect(() => {
    const fetchPlatformConfig = async () => {
      setIsLoading(true);
      await Promise.all([
        dispatch(getCrmConfigurationPreferences()),
        dispatch(getInvoiceSequence()),
      ])
        .catch((error) => {
          notyf.open({
            type: "danger",
            message: error,
            duration: 5000,
            ripple: true,
            dismissible: false,
            position: {
              x: "center",
              y: "top",
            },
          });
        })
        .finally(() => {
          setTimeout(() => {
            setIsLoading(false);
          }, 4500);
        });
    };

    if (activeUser?.id && activeTab === "#CrmConfig") {
      fetchPlatformConfig();
    }
  }, [activeUser, activeTab, dispatch, notyf]);

  const initialValues = {
    type: crmConfigurationPreferences?.type ?? "",
    url: crmConfigurationPreferences?.url ?? "",
    authType: crmConfigurationPreferences?.authType ?? "",
    username: crmConfigurationPreferences?.username ?? "",
    changePassword: false,
    password: "",
    changeToken: false,
    token: "",
  };

  const invoiceSequenceInitialValues = {
    nextNumber: invoiceSequence?.nextNumber ?? "",
  };

  const validation = Yup.object().shape({
    type: Yup.string().required("Type is required"),
    url: Yup.string().required("Url is required"),
    authType: Yup.string().required("Authentication type is required"),
    username: Yup.string().when("authType", {
      is: "BASIC",
      then: () => Yup.string().required("Username is required"),
      otherwise: Yup.string().notRequired(),
    }),
    password: Yup.string().when("changePassword", {
      is: true,
      then: () => Yup.string().required("Password is required"),
      otherwise: Yup.string().notRequired(),
    }),
    confirmPassword: Yup.string().oneOf(
      [Yup.ref("password"), null],
      "Passwords must match"
    ),
    token: Yup.string().when("changeToken", {
      is: true,
      then: () => Yup.string().required("Token is required"),
      otherwise: Yup.string().notRequired(),
    }),
  });

  const invoiceSequenceValidation = Yup.object().shape({
    nextNumber: Yup.number().required("Required"),
  });

  if (isLoading) {
    return (
      <Card>
        <Card.Body>
          <Loader />
        </Card.Body>
      </Card>
    );
  }

  return (
    <React.Fragment>
      <Card>
        <Card.Header>
          <Card.Title>{t("settings.crmConfig.title")}</Card.Title>
        </Card.Header>
        <Card.Body>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={async (values, { setValues }) => {
              const data = {
                type: values.type,
                url: values.url,
                authType: values.authType,
              };
              if (values.authType === "BASIC") {
                data.username = values.username;
                if (values.changePassword) {
                  data.password = values.password;
                }
              } else if (values.authType === "API_KEY") {
                if (values.changeToken) {
                  data.token = values.token;
                }
              }
              await dispatch(updateCrmConfigPreferences({ data: data }))
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "CRM Configuration updated successfully",
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: "Something went wrong",
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .finally(() => {
                  setValues(initialValues);
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setValues,
              isSubmitting,
              setFieldValue,
            }) => {
              const resetToInitial = () => setValues(initialValues);
              return (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("settings.crmConfig.type")}</Form.Label>
                    <Form.Select
                      name="type"
                      value={values.type}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={Boolean(touched.type && errors.type)}
                    >
                      <option value="">
                        {t("settings.crmConfig.selectType")}
                      </option>
                      <option value="Perfex CRM">Perfex</option>
                    </Form.Select>
                    {!!touched.type && (
                      <Form.Control.Feedback type="invalid">
                        {errors.type}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>URL</Form.Label>
                    <Form.Control
                      type="text"
                      name="url"
                      value={values.url}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={Boolean(touched.url && errors.url)}
                    />
                    {!!touched.url && (
                      <Form.Control.Feedback type="invalid">
                        {errors.url}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("settings.crmConfig.authType")}</Form.Label>
                    <Form.Select
                      name="authType"
                      value={values.authType}
                      onChange={(e) => {
                        if (e.target.value === "BASIC") {
                          setFieldValue("token", "");
                        } else {
                          setFieldValue("username", "");
                        }
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      isInvalid={Boolean(touched.authType && errors.authType)}
                    >
                      <option value="">
                        {t("settings.crmConfig.selectAuthType")}
                      </option>
                      <option value="BASIC">Basic</option>
                      <option value="API_KEY">API Key</option>
                    </Form.Select>
                    {!!touched.authType && (
                      <Form.Control.Feedback type="invalid">
                        {errors.authType}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                  <Collapse in={values.authType === "BASIC"} dimension="height">
                    <div>
                      <Form.Group className="mb-3">
                        <Form.Label>Username</Form.Label>
                        <Form.Control
                          type="text"
                          name="username"
                          value={values.username}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={Boolean(
                            touched.username && errors.username
                          )}
                        />
                        {!!touched.username && (
                          <Form.Control.Feedback type="invalid">
                            {errors.username}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          className="change-password-checkbox"
                          label={t("settings.crmConfig.changePassword")}
                          name="changePassword"
                          checked={values.changePassword}
                          onChange={(e) =>
                            setFieldValue("changePassword", e.target.checked)
                          }
                          style={{
                            "--dynamic-bg-color": backgroundColor,
                            "--dynamic-font-color": fontColor,
                          }}
                        />
                      </Form.Group>
                      <Collapse in={values.changePassword} dimension="height">
                        <div>
                          <Form.Group className="mb-3">
                            <Form.Label>Password</Form.Label>
                            <Form.Control
                              type="password"
                              name="password"
                              value={values.password}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={Boolean(
                                touched.password && errors.password
                              )}
                            />
                            {!!touched.password && (
                              <Form.Control.Feedback type="invalid">
                                {errors.password}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>Confirm Password</Form.Label>
                            <Form.Control
                              type="password"
                              name="confirmPassword"
                              value={values.confirmPassword}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={Boolean(
                                touched.confirmPassword &&
                                  errors.confirmPassword
                              )}
                            />
                            {!!touched.confirmPassword && (
                              <Form.Control.Feedback type="invalid">
                                {errors.confirmPassword}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                        </div>
                      </Collapse>
                    </div>
                  </Collapse>
                  <Collapse
                    in={values.authType === "API_KEY"}
                    dimension="height"
                  >
                    <div>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          className="change-password-checkbox"
                          label={t("settings.crmConfig.changeToken")}
                          name="changeToken"
                          checked={values.changeToken}
                          onChange={(e) =>
                            setFieldValue("changeToken", e.target.checked)
                          }
                          style={{
                            "--dynamic-bg-color": backgroundColor,
                            "--dynamic-font-color": fontColor,
                          }}
                        />
                      </Form.Group>
                      <Collapse in={values.changeToken} dimension="height">
                        <Form.Group className="mb-3">
                          <Form.Label>Token</Form.Label>
                          <Form.Control
                            type="text"
                            name="token"
                            value={values.token}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={Boolean(touched.token && errors.token)}
                          />
                          {!!touched.token && (
                            <Form.Control.Feedback type="invalid">
                              {errors.token}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Collapse>
                    </div>
                  </Collapse>
                  {isDesktop && (
                    <div className="large-screen-form-footer">
                      <Row>
                        <Col md="auto" className="ms-auto">
                          <Button
                            type="submit"
                            variant="primary"
                            style={{
                              "--dynamic-bg-color": backgroundColor,
                              "--dynamic-font-color": fontColor,
                              border: "none",
                            }}
                          >
                            {t("settings.purchaseOrderConfig.save")}
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  )}
                  {!isDesktop && (
                    <div className="mt-3 d-flex justify-content-center">
                      <Button
                        type="submit"
                        variant="primary"
                        style={{
                          "--dynamic-bg-color": backgroundColor,
                          "--dynamic-font-color": fontColor,
                          border: "none",
                          padding: "10px",
                          width: "100%",
                        }}
                      >
                        {t("settings.purchaseOrderConfig.save")}
                      </Button>
                    </div>
                  )}
                </Form>
              );
            }}
          </Formik>
        </Card.Body>
      </Card>
      <Card>
        <Card.Header>
          <Card.Title>Invoice Sequence</Card.Title>
        </Card.Header>
        <Card.Body>
          <Formik
            enableReinitialize
            initialValues={invoiceSequenceInitialValues}
            validationSchema={invoiceSequenceValidation}
            onSubmit={async (values, { setValues }) => {
              const data = {
                nextNumber: values.nextNumber,
              };
              await dispatch(updateInvoiceSequence({ data: data }))
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "Invoice sequence updated successfully",
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: "Something went wrong",
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .finally(() => {
                  setValues(initialValues);
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setValues,
              isSubmitting,
              setFieldValue,
            }) => {
              const resetToInitial = () => setValues(initialValues);
              return (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Next Invoice Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="nextNumber"
                      value={values.nextNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={Boolean(
                        touched.nextNumber && errors.nextNumber
                      )}
                    />
                    {!!touched.nextNumber && (
                      <Form.Control.Feedback type="invalid">
                        {errors.nextNumber}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                  {isDesktop && (
                    <div className="large-screen-form-footer">
                      <Row>
                        <Col md="auto" className="ms-auto">
                          <Button
                            type="submit"
                            variant="primary"
                            style={{
                              "--dynamic-bg-color": backgroundColor,
                              "--dynamic-font-color": fontColor,
                              border: "none",
                            }}
                          >
                            {t("settings.purchaseOrderConfig.save")}
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  )}
                  {!isDesktop && (
                    <div className="mt-3 d-flex justify-content-center">
                      <Button
                        type="submit"
                        variant="primary"
                        style={{
                          "--dynamic-bg-color": backgroundColor,
                          "--dynamic-font-color": fontColor,
                          border: "none",
                          padding: "10px",
                          width: "100%",
                        }}
                      >
                        {t("settings.purchaseOrderConfig.save")}
                      </Button>
                    </div>
                  )}
                </Form>
              );
            }}
          </Formik>
        </Card.Body>
      </Card>
    </React.Fragment>
  );
};

export default CrmConfigurationSettings;
